/**
 * Supabase Notes API Service
 *
 * Handles all note/activity operations for leads.
 */

import { supabase } from './client';
import type { Note, NoteInsert, NoteUpdate } from './database.types';

// Frontend Note type (camelCase)
export interface NoteData {
  id: string;
  createdAt: string;
  updatedAt: string;
  leadId: string | null;
  type: 'call' | 'email' | 'text' | 'meeting' | 'quote_sent' | 'site_visit';
  content: string;
  date: string;
  userId: string | null;
}

// Map database note to frontend format
const mapNoteToData = (note: Note): NoteData => ({
  id: note.id,
  createdAt: note.created_at,
  updatedAt: note.updated_at,
  leadId: note.lead_id,
  type: note.type,
  content: note.content,
  date: note.date,
  userId: note.user_id,
});

// Map frontend note to database format
const mapDataToNote = (data: Partial<NoteData>): Partial<NoteInsert> => {
  const mapped: Partial<NoteInsert> = {};

  if (data.leadId !== undefined) mapped.lead_id = data.leadId;
  if (data.type !== undefined) mapped.type = data.type;
  if (data.content !== undefined) mapped.content = data.content;
  if (data.date !== undefined) mapped.date = data.date;
  if (data.userId !== undefined) mapped.user_id = data.userId;

  return mapped;
};

export interface GetNotesParams {
  leadId?: string;
  type?: NoteData['type'];
  limit?: number;
  offset?: number;
}

export interface GetNotesResponse {
  data: NoteData[];
  count: number;
}

class NotesApi {
  /**
   * Get all notes with optional filters
   */
  async getAll(params: GetNotesParams = {}): Promise<GetNotesResponse> {
    let query = supabase
      .from('notes')
      .select('*', { count: 'exact' });

    // Apply filters
    if (params.leadId) {
      query = query.eq('lead_id', params.leadId);
    }

    if (params.type) {
      query = query.eq('type', params.type);
    }

    // Apply sorting (newest first)
    query = query.order('date', { ascending: false });

    // Apply pagination
    if (params.limit) {
      query = query.limit(params.limit);
    }

    if (params.offset) {
      query = query.range(params.offset, params.offset + (params.limit || 25) - 1);
    }

    const { data, error, count } = await query;

    if (error) {
      // console.error('Failed to fetch notes:', error);
      throw error;
    }

    return {
      data: (data || []).map(mapNoteToData),
      count: count || 0,
    };
  }

  /**
   * Get notes for a specific lead
   */
  async getByLead(leadId: string): Promise<NoteData[]> {
    const { data, error } = await supabase
      .from('notes')
      .select('*')
      .eq('lead_id', leadId)
      .order('date', { ascending: false });

    if (error) {
      // console.error('Failed to fetch notes by lead:', error);
      throw error;
    }

    return (data || []).map(mapNoteToData);
  }

  /**
   * Get a single note by ID
   */
  async getById(id: string): Promise<NoteData | null> {
    const { data, error } = await supabase
      .from('notes')
      .select('*')
      .eq('id', id)
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
        return null; // Not found
      }
      // console.error('Failed to fetch note:', error);
      throw error;
    }

    return data ? mapNoteToData(data) : null;
  }

  /**
   * Create a new note
   */
  async create(noteData: Omit<NoteData, 'id' | 'createdAt' | 'updatedAt'>): Promise<NoteData> {
    const dbData = mapDataToNote(noteData) as NoteInsert;

    const { data, error } = await supabase
      .from('notes')
      .insert(dbData as any)
      .select()
      .single();

    if (error) {
      // console.error('Failed to create note:', error);
      throw error;
    }

    return mapNoteToData(data as Note);
  }

  /**
   * Update an existing note
   */
  async update(id: string, noteData: Partial<NoteData>): Promise<NoteData> {
    const dbData = mapDataToNote(noteData) as NoteUpdate;

    const { data, error } = await (supabase
      .from('notes')
      .update(dbData as any)
      .eq('id', id)
      .select()
      .single() as any);

    if (error) {
      // console.error('Failed to update note:', error);
      throw error;
    }

    return mapNoteToData(data as Note);
  }

  /**
   * Delete a note
   */
  async delete(id: string): Promise<void> {
    const { error } = await supabase
      .from('notes')
      .delete()
      .eq('id', id);

    if (error) {
      // console.error('Failed to delete note:', error);
      throw error;
    }
  }

  /**
   * Delete all notes for a lead (used when deleting a lead)
   */
  async deleteByLead(leadId: string): Promise<void> {
    const { error } = await supabase
      .from('notes')
      .delete()
      .eq('lead_id', leadId);

    if (error) {
      // console.error('Failed to delete notes by lead:', error);
      throw error;
    }
  }

  /**
   * Get recent activity across all leads
   */
  async getRecentActivity(limit: number = 10): Promise<NoteData[]> {
    const { data, error } = await supabase
      .from('notes')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(limit);

    if (error) {
      // console.error('Failed to fetch recent activity:', error);
      throw error;
    }

    return (data || []).map(mapNoteToData);
  }
}

export const notesApi = new NotesApi();

// Note type labels for display
export const noteTypeLabels: Record<NoteData['type'], string> = {
  call: 'Phone Call',
  email: 'Email',
  text: 'Text Message',
  meeting: 'Meeting',
  quote_sent: 'Quote Sent',
  site_visit: 'Site Visit',
};

// Note type icons (for UI reference)
export const noteTypeIcons: Record<NoteData['type'], string> = {
  call: 'phone',
  email: 'mail',
  text: 'message-square',
  meeting: 'users',
  quote_sent: 'file-text',
  site_visit: 'map-pin',
};
